/**
 * Shared expiry-rule lookup logic for milk_expiry_rules.
 *
 * Used by ReceiveMilkDialog, EditDeleteMilkDialog, and ReceiveMultipleMilkDialog
 * (origin 'mother' | 'donor') for MOM/Donor milk, and available for FORMULA
 * (by form_factor) and FEED (by feed_type) lookups.
 *
 * Post-migration data model (see migration 20260819000000_update_milk_expiry_rules_columns):
 *  - MILK rows: category ('MOM' | 'Donor' | 'Donor Pasteurised') + milk_type_label
 *    ('Fresh' | 'Thawed' | 'Pasteurised') + storage_condition, the physical
 *    "Location" bucket ('Fridge' | 'Freezer' | 'Deep Freezer') + state, the
 *    derived storage-state label ('REFRIGERATED' | 'Frozen' | 'Deep Frozen' |
 *    'Thawed'). The rule that applies is determined by
 *    **Origin + Type + Location** (category + milk_type_label +
 *    storage_condition) — NOT by milk status alone. "Fresh" milk stored in a
 *    Freezer resolves to a different row (and duration) than "Fresh" milk
 *    stored in a Fridge or Deep Freezer, even though the status is identical.
 *    See `findExpiryRule` / `getLocationBucket`.
 *  - FORMULA rows: category='FORMULA', keyed by form_factor (Powder, Human Milk
 *    Fortifier, Concentrate, Ready To Feed, Ready To Hang, Sterile Water).
 *    milk_type_label/storage_condition/state are NULL for these rows.
 *  - FEED rows: category='FEED'. The seeded reference row has no feed_type set
 *    (a single hospital-wide rule); hospitals may configure additional rows with
 *    feed_type ('PLAIN' | 'FORTIFIED') for more granular matching.
 *  - `expiry_duration` is NOT populated by the reference-data seed (NULL) — hospitals
 *    configure it via Settings, so callers must treat "rule found but unconfigured"
 *    (duration null) the same as "no rule" (return null, no fallback).
 */
import i18next from 'i18next';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES, config } from '@/core/config';
import { getMilkStorageTypeLabel } from '@/shared/utils/location-label';

export type ExpiryRuleRow = {
  milk_type_label: string | null;
  category: string;
  storage_condition: string | null;
  state: string | null;
  form_factor?: string | null;
  feed_type?: string | null;
  expiry_duration: number | null;
  unit: string;
  is_active: boolean;
};

export type MilkOrigin = 'mother' | 'donor';
export type ExpiryType = 'MILK' | 'FEED' | 'FORMULA';

/**
 * Maps the app's milk-status enum to the `milk_type_label` stored on
 * milk_expiry_rules rows. FRESH and FROZEN both correspond to the 'Fresh'
 * label — the row that actually applies (Refrigerated/Frozen/Deep Frozen) is
 * disambiguated by the container's physical storage **location**, not by
 * this status value (see `findExpiryRule`).
 */
const MILK_STATUS_TO_LABEL: Record<string, string> = {
  FRESH:       'Fresh',
  FROZEN:      'Fresh',
  THAWED:      'Thawed',
  PASTEURISED: 'Pasteurised',
};

/**
 * Maps a storage location's `storage_type` (as returned by
 * GET /settings/api/v1/storage-locations/) to the "Location" bucket used by
 * milk_expiry_rules.storage_condition ('Fridge' | 'Freezer' | 'Deep Freezer').
 * Different endpoints/seed data have been observed to return either the
 * enum form ('REFRIGERATOR') or the human label ('FRIDGE') for the same
 * concept, so both are accepted — lookup is case-insensitive.
 */
const STORAGE_TYPE_TO_LOCATION_BUCKET: Record<string, string> = {
  REFRIGERATOR: 'Fridge',
  FRIDGE:       'Fridge',
  FREEZER:      'Freezer',
  DEEP_FREEZER: 'Deep Freezer',
};

/** Converts a storage location's `storage_type` into its expiry-rule Location bucket. */
export function getLocationBucket(storageType?: string | null): string | undefined {
  return storageType ? STORAGE_TYPE_TO_LOCATION_BUCKET[storageType.toUpperCase()] : undefined;
}

// ---------------------------------------------------------------------------
// Receive Milk Expiry Rules — base timestamp resolution
// ---------------------------------------------------------------------------
// Shared by ReceiveMilkDialog and ReceiveMultipleMilkDialog so both surfaces
// determine the shelf-life "clock start" identically. The milk batch/container
// CREATION timestamp must never be used here — only the container's Pump Date
// or the current moment (time of receiving), chosen per Milk Status + the
// selected Location's storage type:
//
//   Frozen + Freezer       -> Pump Date
//   Frozen + Deep Freezer  -> Pump Date
//   Thawed + Refrigerator  -> Current Timestamp (time of receiving)
//   Fresh  + Refrigerator  -> Pump Date
//   Fresh  + Freezer       -> Pump Date
//   Fresh  + Deep Freezer  -> Pump Date
//
// Thawed milk may only be stored in a Fridge (see the THAWED-location
// restriction in both dialogs), so its base timestamp does not depend on
// `locationType`. Frozen milk may never be stored in a Fridge (see the
// FROZEN-location restriction in both dialogs), so it always uses Pump Date.

export interface GetExpiryBaseTimestampParams {
  /** Selected Milk Status ('FRESH' | 'FROZEN' | 'THAWED'). */
  milkStatus: string | null | undefined;
  /** Storage Location Type/bucket for the selected Location — see `getLocationBucket` ('Fridge' | 'Freezer' | 'Deep Freezer'). */
  locationType: string | null | undefined;
  /** Selected Pump/Pumped Date. */
  pumpDate: Date | null | undefined;
  /** Instant to use as "now" for Current-Timestamp-based combinations. Defaults to `new Date()`. */
  currentTimestamp?: Date;
}

/**
 * Determines the expiry base timestamp (the instant the configured
 * shelf-life duration is added to) for a milk container being received, per
 * the Receive Milk Expiry Rules above.
 *
 * Any milk status outside FRESH/FROZEN/THAWED (e.g. PASTEURISED, which is
 * never selectable from the Receive Milk dialogs but is still resolvable via
 * `getExpiryOffsetHoursForStatus`) falls back to Pump Date, matching the
 * behavior for FRESH — this is a deliberate, safe default rather than a rule
 * from the table above, since no receiving workflow currently produces that
 * status.
 *
 * Returns `null` when there isn't enough information to decide yet — a
 * missing Milk Status, or a Pump-Date-based combination with no Pump Date
 * selected — callers should treat this the same as "not yet calculable" and
 * leave the Expiry Date blank, matching existing dialog behavior.
 */
export function getExpiryBaseTimestamp({
  milkStatus,
  // Retained on the params type for call-site compatibility — no longer
  // affects the result now that Frozen milk can never be stored in a Fridge.
  locationType: _locationType,
  pumpDate,
  currentTimestamp = new Date(),
}: GetExpiryBaseTimestampParams): Date | null {
  if (!milkStatus) return null;

  if (milkStatus === 'THAWED') return currentTimestamp;

  return pumpDate ?? null;
}

// ---------------------------------------------------------------------------
// Deep Freezer location availability (Settings > Expiry Rules driven)
// ---------------------------------------------------------------------------
// Whether a Deep Freezer storage location may be offered/selected in a location
// dropdown is driven entirely by the hospital's configured "Deep Frozen" state
// expiry rule for the applicable milk type — NEVER a hardcoded/global flag.
// MOM (Mother Milk) and Donor Milk have SEPARATE Deep Freezer rules, matching
// the two independent "Deep Frozen" toggle groups in
// apps/tms-app-soho/src/modules/settings/components/ExpiryRulesTable.vue
// (DEEP_FROZEN_TOGGLE_GROUPS: ['Mother Milk'] and ['Donor Milk', 'Donor
// Pasteurised Milk']). This is the single shared implementation of that rule —
// every location dropdown in the app (Receive Mother/Donor Milk, Receive
// Multiple, Edit/Delete Milk, Convert to Donor Milk, Milk Batch creation, Move/
// Transfer Inventory) must filter through `filterLocationsByDeepFreezerRule` /
// `getAvailableLocations` rather than re-implementing this check.

/** Milk type a location selection is being made for. */
export type MilkTypeParam = 'MOM' | 'DONOR';

/** Workflow context a location dropdown is being populated for. Accepted for API parity /
 *  future workflow-specific rules — Deep Freezer availability today depends only on milk type. */
export type LocationWorkflow = 'RECEIVING' | 'BATCH_CREATION' | 'TRANSFER' | 'GENERAL';

/** Minimal shape any location option must satisfy to be filtered by this module. */
export type SelectableLocation = {
  value: string;
  label: string;
  storageType?: string | null;
};

/**
 * milk_expiry_rules `category` value(s) whose "Deep Frozen" state row governs
 * each milk type's Deep Freezer availability. DONOR spans both the raw
 * ('Donor') and pasteurised ('Donor Pasteurised') categories because the
 * Settings toggle enables/disables both together as one group — checking
 * either resolves the same answer.
 */
const DEEP_FREEZER_RULE_CATEGORIES: Record<MilkTypeParam, string[]> = {
  MOM: ['MOM'],
  DONOR: ['Donor', 'Donor Pasteurised'],
};

/**
 * Whether the Deep Freezer expiry rule is enabled (active) for the given milk
 * type, from already-fetched MILK expiry rows (`getExpiryRows('MILK')`).
 */
export function isDeepFreezerRuleEnabled(rows: ExpiryRuleRow[], milkType: MilkTypeParam): boolean {
  const categories = DEEP_FREEZER_RULE_CATEGORIES[milkType];
  return rows.some((r) => categories.includes(r.category) && r.storage_condition === 'Deep Freezer' && r.is_active);
}

/**
 * Whether a location's `storageType` field represents "Deep Freezer",
 * accepting either the raw enum form ('DEEP_FREEZER', via `getLocationBucket`)
 * OR an already-localized display label (e.g. "Deep Freezer" / "Tiefkühler"),
 * as produced by `getMilkStorageTypeLabel('DEEP_FREEZER')`. Some location
 * lists (e.g. `pasteurization.store.ts`'s `storageLocations`, consumed by
 * ApproveMilkBatchDialog/PrepareFeedView) are pre-translated at fetch time
 * rather than carrying the raw enum, so comparing against the *current*
 * locale's translated label keeps this check correct for either shape.
 */
export function isDeepFreezerStorageType(storageType?: string | null): boolean {
  if (!storageType) return false;
  if (getLocationBucket(storageType) === 'Deep Freezer') return true;
  return storageType === getMilkStorageTypeLabel('DEEP_FREEZER');
}

/**
 * Filters a list of selectable storage locations, removing Deep Freezer
 * locations whenever the applicable milk type's Deep Freezer expiry rule is
 * disabled. Non-Deep-Freezer locations are always returned untouched.
 *
 * `currentValue` (optional) keeps a single already-selected location visible
 * even if it's a now-disabled Deep Freezer location — this lets historical
 * records that already reference a Deep Freezer location remain viewable
 * (e.g. an edit-mode dialog pre-populated with a container's current
 * location); it does not let the user pick a *different* Deep Freezer option.
 */
export function filterLocationsByDeepFreezerRule<T extends SelectableLocation>(
  locations: T[],
  milkType: MilkTypeParam,
  rows: ExpiryRuleRow[],
  currentValue?: string | null,
): T[] {
  if (isDeepFreezerRuleEnabled(rows, milkType)) return locations;
  return locations.filter((loc) => !isDeepFreezerStorageType(loc.storageType) || loc.value === currentValue);
}

/**
 * Shared "get available locations" entry point:
 *   getAvailableLocations({ milkType: 'MOM', workflow: 'RECEIVING', locations })
 * Fetches (cached) MILK expiry rows and filters the given locations by the
 * applicable milk type's Deep Freezer rule. `locations` is accepted as a
 * parameter (rather than fetched internally) so this helper composes with
 * every existing location source in the app (storage-locations API, module
 * stores, cached option lists) without introducing a second, conflicting
 * fetch/cache path.
 */
export async function getAvailableLocations<T extends SelectableLocation>(params: {
  milkType: MilkTypeParam;
  workflow: LocationWorkflow;
  locations: T[];
  /** Preserve this already-selected value even if it's a disabled Deep Freezer location. */
  currentValue?: string | null;
}): Promise<T[]> {
  const rows = await getExpiryRows('MILK');
  return filterLocationsByDeepFreezerRule(params.locations, params.milkType, rows, params.currentValue);
}

// Cache for expiry rule rows, keyed by expiry_type — fetched once per page session per
// type, reused for subsequent lookups (across all callers) to avoid redundant API calls.
const _expiryRowsCache = new Map<ExpiryType, ExpiryRuleRow[]>();
const _expiryRowsFetchPromise = new Map<ExpiryType, Promise<ExpiryRuleRow[]>>();

// Bumped by resetExpiryRulesCache() so that a GET already in flight when the
// cache is invalidated (e.g. a Settings update firing mid-request) can tell
// its response is stale and must not repopulate the cache once it resolves.
let _expiryRowsCacheGeneration = 0;

/**
 * Fetch (and cache) the hospital's expiry rule rows from
 * GET /settings/api/v1/milkExpiryRules/?hospital_id=...&expiry_type=<type>.
 * Defaults to 'MILK' for backwards compatibility with existing mother/donor callers.
 */
export async function getExpiryRows(expiryType: ExpiryType = 'MILK'): Promise<ExpiryRuleRow[]> {
  const cached = _expiryRowsCache.get(expiryType);
  if (cached) return cached;

  let pending = _expiryRowsFetchPromise.get(expiryType);
  if (!pending) {
    const requestGeneration = _expiryRowsCacheGeneration;
    const api = getApiClient();
    pending = api
      .get(`${API_ROUTES.SETTINGS}/milkExpiryRules/`, {
        params: { hospital_id: config.hospitalId, expiry_type: expiryType },
      })
      .then((res) => {
        const rows: ExpiryRuleRow[] = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        // Only commit to the cache if it hasn't been invalidated since this
        // request was issued — otherwise a response that resolves after a
        // reset (e.g. a Settings update) would silently repopulate the
        // cache with pre-update rules for the next lookup.
        if (requestGeneration === _expiryRowsCacheGeneration) {
          _expiryRowsCache.set(expiryType, rows);
        }
        return rows;
      })
      .finally(() => {
        // Only clear our own entry — a reset in the meantime may have
        // already cleared the map and let a newer request claim this key.
        if (_expiryRowsFetchPromise.get(expiryType) === pending) {
          _expiryRowsFetchPromise.delete(expiryType);
        }
      });
    _expiryRowsFetchPromise.set(expiryType, pending);
  }
  return pending;
}

/** Forces the next `getExpiryRows()` call (for any expiry type) to re-fetch (e.g. on hospital switch). */
export function resetExpiryRulesCache(): void {
  _expiryRowsCacheGeneration += 1;
  _expiryRowsCache.clear();
  _expiryRowsFetchPromise.clear();
}

/**
 * Find the matching milk_expiry_rules row for a given origin (mother/donor),
 * milk status (FRESH/FROZEN/THAWED/PASTEURISED) AND the container's physical
 * storage location bucket ('Fridge' | 'Freezer' | 'Deep Freezer' — see
 * `getLocationBucket`).
 *
 * The rule lookup is Origin + Type + Location (category + milk_type_label +
 * storage_condition), matching the Expiry Rules configuration matrix at
 * /app-settings/expiry-rules. Milk status alone is NOT sufficient — e.g.
 * "Fresh" milk stored in a Freezer must resolve to the Frozen/Freezer rule,
 * not the Refrigerated/Fridge rule, even though the status is the same.
 *
 * Thawed rules are always configured against the Fridge location regardless
 * of which physical location the container is stored in — the caller does
 * not need to supply a location for THAWED status.
 *
 * `isPasteurisedCategory` forces the 'Donor Pasteurised' category regardless
 * of `statusValue` — needed for a batch bottle transitioning to THAWED (its
 * 'Donor Pasteurised' + 'Thawed' + 'Fridge' row, e.g. migration
 * 20260819000000, would otherwise be missed since only a literal PASTEURISED
 * statusValue maps to that category by default; a batch bottle's status CAN
 * read THAWED — via resolveMilkStatusValue's location-based thaw signal — so
 * `statusValue === 'PASTEURISED'` alone is not enough to identify it here).
 *
 * Returns undefined for unrecognized status values, a missing/unmapped
 * location, or when no active rule matches — callers must not fall back
 * silently.
 */
export function findExpiryRule(
  rows: ExpiryRuleRow[],
  origin: MilkOrigin,
  statusValue: string,
  locationBucket?: string | null,
  isPasteurisedCategory?: boolean,
): ExpiryRuleRow | undefined {
  const label = MILK_STATUS_TO_LABEL[statusValue];
  if (!label) return undefined;

  const categoryValue = statusValue === 'PASTEURISED' || isPasteurisedCategory
    ? 'Donor Pasteurised'
    : origin === 'donor' ? 'Donor' : 'MOM';
  // Thawed rules are keyed to the Fridge location regardless of the
  // container's actual current physical location.
  const effectiveLocation = label === 'Thawed' ? 'Fridge' : locationBucket;
  if (!effectiveLocation) return undefined;

  return rows.find(
    (r) =>
      r.milk_type_label === label &&
      r.category === categoryValue &&
      r.storage_condition === effectiveLocation &&
      r.is_active,
  );
}

/**
 * Find the matching FORMULA rule for a given form_factor (Powder, Concentrate,
 * Ready To Feed, Ready To Hang, Human Milk Fortifier, Sterile Water).
 * Matched case-insensitively — the product catalog's Form field uses
 * "Ready to Feed"/"Ready to Hang" (lowercase "to"), while the Expiry Rules
 * settings enum uses "Ready To Feed"/"Ready To Hang" (capital "To").
 */
export function findFormulaExpiryRule(
  rows: ExpiryRuleRow[],
  formFactor: string,
): ExpiryRuleRow | undefined {
  const target = formFactor.trim().toLowerCase();
  return rows.find(
    (r) => r.category === 'FORMULA' && r.is_active && (r.form_factor ?? '').trim().toLowerCase() === target,
  );
}

/**
 * Find the matching FEED rule. Prefers a row with a matching feed_type
 * ('PLAIN' | 'FORTIFIED') when provided; falls back to the single
 * hospital-wide FEED row (feed_type unset) otherwise.
 */
export function findFeedExpiryRule(
  rows: ExpiryRuleRow[],
  feedType?: string,
): ExpiryRuleRow | undefined {
  const feedRows = rows.filter((r) => r.category === 'FEED' && r.is_active);
  if (feedType) {
    const match = feedRows.find((r) => r.feed_type === feedType);
    if (match) return match;
  }
  return feedRows.find((r) => !r.feed_type);
}

/** Converts an expiry_duration/unit pair into hours. */
export function expiryDurationToHours(expiry_duration: number, unit: string): number {
  return unit === 'HOURS'
    ? expiry_duration
    : unit === 'DAYS'
    ? expiry_duration * 24
    : unit === 'MONTHS'
    ? expiry_duration * 30 * 24
    : expiry_duration;
}

/**
 * Format an hours offset into a human-readable duration string, e.g.
 * "2 days", "1 day 6 hours (30 hours)", "48 hours". Shared by
 * ReceiveMilkDialog, EditDeleteMilkDialog, and AddMilkBatchDialog's
 * shelf-life hints so all three surfaces render duration text identically.
 */
export function formatDurationFromHours(h: number): string {
  const days = Math.floor(h / 24);
  const remainingHours = h % 24;
  const hoursPart = i18next.t('inventory.durationHours', { count: h });
  if (days === 0) return hoursPart;
  const daysPart = i18next.t('inventory.durationDays', { count: days });
  if (remainingHours === 0) return `${daysPart} (${hoursPart})`;
  const remainingHoursPart = i18next.t('inventory.durationHours', { count: remainingHours });
  return `${daysPart} ${remainingHoursPart} (${hoursPart})`;
}

/**
 * Convenience wrapper: fetch MILK rows (cached) + find match (origin + status +
 * location) + convert to hours. Returns null when there's no active hospital,
 * no matching/configured rule, or the fetch fails.
 */
export async function getExpiryOffsetHoursForStatus(
  origin: MilkOrigin,
  statusValue: string,
  locationBucket?: string | null,
): Promise<number | null> {
  if (!config.hospitalId) return null;
  try {
    const rows = await getExpiryRows('MILK');
    const match = findExpiryRule(rows, origin, statusValue, locationBucket);
    return match?.expiry_duration != null ? expiryDurationToHours(match.expiry_duration, match.unit) : null;
  } catch {
    return null;
  }
}

/**
 * Convenience wrapper: fetch FORMULA rows (cached) + find match by form_factor + convert to hours.
 * Returns null when there's no active hospital, no matching/configured rule, or the fetch fails.
 */
export async function getFormulaExpiryOffsetHours(formFactor: string): Promise<number | null> {
  if (!config.hospitalId) return null;
  try {
    const rows = await getExpiryRows('FORMULA');
    const match = findFormulaExpiryRule(rows, formFactor);
    return match?.expiry_duration != null ? expiryDurationToHours(match.expiry_duration, match.unit) : null;
  } catch {
    return null;
  }
}

/**
 * Convenience wrapper: fetch FEED rows (cached) + find match by feed_type (optional,
 * falls back to the single hospital-wide rule) + convert to hours.
 * Returns null when there's no active hospital, no matching/configured rule, or the fetch fails.
 */
export async function getFeedExpiryOffsetHours(feedType?: string): Promise<number | null> {
  if (!config.hospitalId) return null;
  try {
    const rows = await getExpiryRows('FEED');
    const match = findFeedExpiryRule(rows, feedType);
    return match?.expiry_duration != null ? expiryDurationToHours(match.expiry_duration, match.unit) : null;
  } catch {
    return null;
  }
}

/** Converts an expiry_duration/unit pair into days. */
export function expiryDurationToDays(expiry_duration: number, unit: string): number {
  return unit === 'DAYS'
    ? expiry_duration
    : unit === 'HOURS'
    ? expiry_duration / 24
    : unit === 'MONTHS'
    ? expiry_duration * 30
    : expiry_duration;
}

/**
 * Convenience wrapper: fetch FORMULA rows (cached) + find match by form_factor + convert to days.
 * Returns null when there's no active hospital, no matching/configured rule, or the fetch fails.
 */
export async function getFormulaExpiryOffsetDays(formFactor: string): Promise<number | null> {
  if (!config.hospitalId) return null;
  try {
    const rows = await getExpiryRows('FORMULA');
    const match = findFormulaExpiryRule(rows, formFactor);
    return match?.expiry_duration != null ? expiryDurationToDays(match.expiry_duration, match.unit) : null;
  } catch {
    return null;
  }
}

/**
 * Resolve Settings-based defaults for the product registration form's Expiry
 * After Mixing (Hours, FEED — single hospital-wide rule) / Expiry After
 * Opening (Days, FORMULA — keyed by the product's Form/form_factor). Used to
 * prefill AddProductDialog's expiration fields when empty — e.g. a brand new
 * product, or one added from the shared tms-app-product catalog, which never
 * carries a hospital-specific shelf life (see mapCatalogProductToDetail).
 */
/**
 * Resolve Settings-based defaults for the product registration form's Expiry
 * After Mixing (FEED — single hospital-wide rule) / Expiry After Opening
 * (FORMULA — keyed by the product's Form/form_factor). Used to prefill
 * AddProductDialog's expiration fields when empty — e.g. a brand new
 * product, or one added from the shared tms-app-product catalog, which never
 * carries a hospital-specific shelf life (see mapCatalogProductToDetail).
 *
 * Returns the rule's configured duration **and** unit as-is (no unit
 * conversion) — Settings > Expiry Rules now lets FEED/FORMULA rules be
 * configured in either Hours or Days, so the associated unit dropdown must
 * reflect whatever the hospital actually chose.
 */
export async function getExpirationDefaults(
  formFactor: string,
): Promise<{
  afterMixingHours?: string;
  afterMixingUnit?: 'hours' | 'days';
  afterOpeningDays?: string;
  afterOpeningUnit?: 'hours' | 'days';
}> {
  if (!config.hospitalId) return {};
  try {
    const [feedRows, formulaRows] = await Promise.all([
      getExpiryRows('FEED'),
      formFactor ? getExpiryRows('FORMULA') : Promise.resolve([] as ExpiryRuleRow[]),
    ]);
    const feedMatch = findFeedExpiryRule(feedRows);
    const formulaMatch = formFactor ? findFormulaExpiryRule(formulaRows, formFactor) : undefined;
    const toUnit = (unit: string | undefined): 'hours' | 'days' | undefined =>
      unit === 'HOURS' ? 'hours' : unit === 'DAYS' ? 'days' : undefined;
    return {
      afterMixingHours: feedMatch?.expiry_duration != null ? String(feedMatch.expiry_duration) : undefined,
      afterMixingUnit: toUnit(feedMatch?.unit),
      afterOpeningDays: formulaMatch?.expiry_duration != null ? String(formulaMatch.expiry_duration) : undefined,
      afterOpeningUnit: toUnit(formulaMatch?.unit),
    };
  } catch {
    return {};
  }
}
